<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="login-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                </div>

                <h2 class="text-h5 font-weight-bold text-center mb-1">Forgot your password?</h2>
                <p class="text-body-2 text-medium-emphasis text-center mb-6">
                    Enter your email and we'll send you a reset code
                </p>

                <v-form ref="form" v-model="formData.valid" validate-on="input lazy" @submit.prevent="submit">
                    <v-text-field v-model="formData.email" :rules="[vRules.required, vRules.email]"
                                  autofocus color="primary" density="comfortable"
                                  label="Email" prepend-inner-icon="mdi-email-outline"
                                  rounded="lg" type="email" variant="outlined"/>

                    <v-btn :disabled="!formData.valid || processing" :loading="processing" block color="primary"
                           class="mb-4" rounded="lg" size="large" @click="submit">
                        Send Reset Code
                    </v-btn>

                    <p class="text-body-2 text-center mb-0">
                        Remember your password?
                        <a class="text-primary text-decoration-none font-weight-bold cursor-pointer"
                           @click="navigateTo('login')">Sign in</a>
                    </p>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Footer -->
        <div class="login-footer text-caption text-white">
            &copy; {{ new Date().getFullYear() }} SimpliGPS Go by NextEra IoT
        </div>
    </div>
</template>

<script setup>
import {reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {showApiError} from "@/composables/useApiError";
import logoFull from "@/assets/images/logo-full.png"
import {useNavigation} from '@/composables/globals'
import {useSnackbarStore} from "@/stores/snackBarStore";


const {navigateTo} = useNavigation()
const snackbar = useSnackbarStore()
const form = ref(null)
const processing = ref(false)
const formData = reactive({
    email: '',
    valid: false,
})


const submit = () => {
    processing.value = true;
    send('post', 'request-reset-password', formData)
        .then((response) => {
            // Remember the email so the login box is prefilled once the reset is
            // done (mirrors the post-register 'registered_email' flow).
            localStorage.setItem('reset_email', formData.email)
            formData.email = ''
            processing.value = false;
            snackbar.showSnackbar(response.data.message)
            // The unified response has no user.id for a non-existent email (the
            // "if this email exists" message already showed). Only advance to the
            // reset step when the server returned an id - never deref .user.id on a
            // miss, which would throw into .catch and reveal via the error toast
            // that the email is unknown (account enumeration).
            const userId = response?.data?.data?.user?.id
            if (userId) navigateTo(`reset-password`, {id: userId})
        })
        .catch(function (errorResponse) {
            processing.value = false;
            const validationErrors = errorResponse?.response?.data?.errors;
            if (validationErrors && typeof validationErrors === 'object') {
                Object.keys(validationErrors).forEach((key) => {
                    snackbar.showSnackbar(validationErrors[key][0], 'error');
                });
                return;
            }
            showApiError(snackbar, errorResponse, "Couldn't send reset email. Please try again.");
        })
}
</script>

<style scoped>
.wrapper {
    background: url('@/assets/images/login-bg.png') rgba(38, 35, 90, 0.65);
    background-blend-mode: multiply;
    background-size: cover;
    background-position: center;
    position: relative;
}

.login-card {
    width: 100%;
    max-width: 440px;
    margin: 16px;
}

.login-footer {
    position: absolute;
    bottom: 16px;
    opacity: 0.8;
}
</style>
